/**
 * Scheduled Placement — two-phase pipeline for server-authoritative placement.
 *
 * Architecture:
 *   Phase 1: planScheduledPlacements — places eligible pending games deterministically
 *            into empty slots before matchmaking runs.
 *   Phase 2: planMatchmakingPlacements — fills remaining slots with twoStageEngine;
 *            called by runMatchmakingFromFrozenInput.
 *
 * Rule: the planner is the sole authority for placement decisions.
 *   - No DB reads here.
 *   - No eligibility checks in RPC or DB — only structural invariants.
 *   - buildOccupiedPlayerIds is the single exclusion model used by both phases.
 *
 * Sort contract (must match SQL in _get_pending_scheduled_games_json):
 *   ORDER BY order_index ASC, created_at ASC, id ASC
 *   Frozen input from DB arrives pre-sorted; JS sort is a determinism guard.
 */

// ---------------------------------------------------------------------------
// buildOccupiedPlayerIds
// ---------------------------------------------------------------------------

/**
 * Single exclusion model — used by both scheduled and matchmaking planners.
 *
 * @param {object} baseState — DB snapshot (never mutated)
 * @param {Array<{ team1: string[], team2: string[] }>}  baseState.activeCourts
 * @param {Array<{ team1: string[], team2: string[], slotIndex: number }>} baseState.upcomingSlots
 * @param {Array<{ team1: string[], team2: string[] }>} [placementsSoFar=[]]
 *   Scheduled + matchmaking placements accumulated this run.
 *   Kept separate from baseState so callers can reason about committed vs projected.
 * @returns {Set<string>}
 */
export function buildOccupiedPlayerIds(baseState, placementsSoFar = []) {
  const ids = new Set();

  function addRoster(obj) {
    for (const id of [...(obj?.team1 || []), ...(obj?.team2 || [])]) {
      if (id) ids.add(id);
    }
  }

  for (const court of baseState.activeCourts || []) addRoster(court);
  for (const slot of baseState.upcomingSlots || []) addRoster(slot);
  for (const placement of placementsSoFar) addRoster(placement);

  return ids;
}

// ---------------------------------------------------------------------------
// isScheduledGameEligible
// ---------------------------------------------------------------------------

/**
 * Single shared predicate for scheduled game eligibility.
 * Used by planScheduledPlacements and tests. Not in RPC or DB.
 *
 * @param {object}      game                — scheduled_games row (from frozen input)
 * @param {boolean}     game.roster_complete — DB GENERATED column, included in frozen input
 * @param {string[]}   [game.roster_player_ids] — canonical player array from DB (optional;
 *                       falls back to individual FK columns when absent for compatibility)
 * @param {Set<string>} occupiedPlayerIds   — from buildOccupiedPlayerIds
 * @param {object}      playersById         — { [id]: playerRow }
 * @returns {boolean}
 */
export function isScheduledGameEligible(game, occupiedPlayerIds, playersById) {
  if (!game?.roster_complete) return false;

  // Derive the roster — prefer roster_player_ids (new column) but fall back
  // to individual FKs so the predicate works on all frozen input shapes.
  const roster = Array.isArray(game.roster_player_ids) && game.roster_player_ids.length > 0
    ? game.roster_player_ids
    : [
        game.team1_player1_id,
        game.team1_player2_id,
        game.team2_player1_id,
        game.team2_player2_id,
      ].filter(Boolean);

  if (roster.length === 0) return false;

  for (const id of roster) {
    if (!playersById[id]) return false;       // player not in session
    if (occupiedPlayerIds.has(id)) return false; // already placed
  }

  return true;
}

// ---------------------------------------------------------------------------
// planScheduledPlacements
// ---------------------------------------------------------------------------

/**
 * Phase 1: deterministic scheduled placement — sequential per-slot greedy.
 *
 * Picks the first eligible scheduled game (by sort contract) for each empty
 * slot in ascending order. No global optimisation.
 *
 * @param {object}   baseState              — DB snapshot
 * @param {number[]} emptySlotIndices       — ascending sorted slot indices to fill
 * @param {object[]} scheduledGames         — pending games, PRE-SORTED by sort contract
 * @param {object}   playersById            — { [id]: playerRow }
 * @returns {{
 *   scheduledPlacements: Array<{
 *     slotIndex: number,
 *     gameId: string,
 *     team1: string[],
 *     team2: string[],
 *     type: 'scheduled',
 *     selectedScheduledGame: object,
 *   }>,
 *   deferred: Array<{ id: string, reason: string }>,
 * }}
 */
export function planScheduledPlacements(baseState, emptySlotIndices, scheduledGames, playersById) {
  // Guard: sort by sort contract (determinism guard; DB should already sort).
  const sortedGames = [...(scheduledGames || [])].sort((a, b) => {
    if (a.order_index !== b.order_index) return (a.order_index ?? 0) - (b.order_index ?? 0);
    const ca = a.created_at ? new Date(a.created_at).getTime() : 0;
    const cb = b.created_at ? new Date(b.created_at).getTime() : 0;
    if (ca !== cb) return ca - cb;
    if (a.id < b.id) return -1;
    if (a.id > b.id) return 1;
    return 0;
  });

  const pendingQueue = [...sortedGames];
  const scheduledPlacements = [];

  const ascending = [...(emptySlotIndices || [])].sort((a, b) => a - b);

  for (const slotIndex of ascending) {
    const occupied = buildOccupiedPlayerIds(baseState, scheduledPlacements);

    const gameIdx = pendingQueue.findIndex((g) => isScheduledGameEligible(g, occupied, playersById));
    if (gameIdx === -1) continue;

    const game = pendingQueue[gameIdx];
    pendingQueue.splice(gameIdx, 1);

    const team1 = [game.team1_player1_id, game.team1_player2_id].filter(Boolean);
    const team2 = [game.team2_player1_id, game.team2_player2_id].filter(Boolean);

    scheduledPlacements.push({
      slotIndex,
      gameId: game.id,
      team1,
      team2,
      type: 'scheduled',
      selectedScheduledGame: game,
    });
  }

  const deferred = pendingQueue.map((g) => {
    const occupied = buildOccupiedPlayerIds(baseState, scheduledPlacements);
    const roster = (Array.isArray(g.roster_player_ids) && g.roster_player_ids.length > 0
      ? g.roster_player_ids
      : [g.team1_player1_id, g.team1_player2_id, g.team2_player1_id, g.team2_player2_id].filter(Boolean)
    );
    let reason = 'no_empty_slot';
    if (!g.roster_complete) reason = 'incomplete_roster';
    else if (roster.some((id) => occupied.has(id))) reason = 'player_occupied';
    return { id: g.id, reason };
  });

  return { scheduledPlacements, deferred };
}
